/**
 * @file        src/pages/account/Reports.jsx
 * @description Admin reports: wallet transactions, payment-gateway transactions
 *              (with status), and refunds & cancellations — filterable by brand
 *              and date range, with CSV export. Backend: /api/reports/*.
 *
 *              AUDIENCE IS THE SCREEN, NOT A FILTER. This component backs two
 *              sidebar entries — Reports (B2C) and Corporate Reports (B2B, see
 *              CorporateReports.jsx) — and each asks only for its own rows.
 * @author      Kinetika Labs
 * @created     2026-07-23
 * @changelog
 *   - 2026-07-24 Claude — Fix "Export failed": stop sending pageSize=10000 (over
 *                          the query schema's max of 1000 → 400). Backend already
 *                          caps CSV rows internally.
 *   - 2026-08-03 Claude — Brand filter options + brand cell labels come from the
 *                          tenant's brand registry (useBrands), not a hardcoded
 *                          Neon/Beyond pair.
 *   - 2026-08-04 Claude — Agent retirement (CR E8): removed the "Agent
 *                          Commission" tab, its fetch, summary tile, and table.
 *                          Corporate (b2b_owner/b2b_user) reporting is unaffected.
 *   - 2026-08-10 Claude — To date can no longer precede From date (matches the
 *                          same min={from} guard already used on the Booking
 *                          List date filters). Console-wide date rule pass.
 *   - 2026-09-15 Claude — API errors read via apiErrorMessage(): the error handler returns {error:{code,message}}, and putting that object into state/toast threw "Objects are not valid as a React child", blanking the whole app.
 *   - 2026-09-15 Claude — SPLIT into Reports (B2C) + Corporate Reports (B2B).
 *                          The "Customer type" picker offering "All (B2C + B2B)"
 *                          is gone: the audience now comes from the screen, is
 *                          always sent, and cannot be changed — the same rule the
 *                          Booking Lists and every pricing screen already follow.
 *                          It also exposed a real gap: the Wallet Transactions
 *                          report had been ACCEPTING `audience` and ignoring it,
 *                          so that tab returned identical rows for every choice
 *                          (fixed in repositories/reports.repo.js).
 *   - 2026-09-24 Claude — Wallet tab's Type column prints the shared wallet
 *                          label (lib/walletTypes), not the raw enum value.
 *   - 2026-09-25 Claude — `keepPreviousData: true` is a React Query **v4** option
 *                          and this workspace is on v5 (5.101), where no such
 *                          option exists and it was silently ignored. So every
 *                          page / filter / search change dropped `data` to
 *                          undefined, the table was swapped for six skeleton rows
 *                          and the panel collapsed ~700px before growing back —
 *                          the "pages flicker / the page moves" report. v5 spells
 *                          it `placeholderData: keepPreviousData` (a FUNCTION
 *                          imported from the package, not a boolean).
 */
import { useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import PageHead from '../../components/PageHead';
import Icon from '../../components/Icon';
import Chip from '../../components/Chip';
import Select from '../../components/Select';
import DatePicker from '../../components/DatePicker';
import Pager from '../../components/Pager';
import TableSkeleton from '../../components/TableSkeleton';
import { get, api, apiErrorMessage } from '../../api/client';
import { useBrands } from '../../api/brands';
import { inr } from '../../lib/format';
import { walletTypeLabel } from '../../lib/walletTypes';
import { toast } from '../../lib/toast';

const money = (v) => (typeof inr === 'function' ? inr(v) : `₹${Number(v || 0).toLocaleString('en-IN')}`);
const dt = (v) => (v ? new Date(v).toLocaleString('en-IN') : '—');
const statusTone = (s) => (/success|refunded|completed|booked/i.test(s) ? 'ok' : /fail|reject/i.test(s) ? 'rej' : 'pend');

const TABS = [
  { key: 'wallet', label: 'Wallet Transactions', endpoint: '/reports/wallet-transactions' },
  { key: 'gateway', label: 'Gateway Transactions', endpoint: '/reports/gateway-transactions' },
  { key: 'refunds', label: 'Refunds & Cancellations', endpoint: '/reports/refunds' },
];

export default function Reports({
  title = 'Reports',
  subtitle = 'Account Management · wallet, gateway, refunds & cancellations',
  // Which audience this SCREEN is. Fixed — see the note in the file header.
  audience = 'b2c',
} = {}) {
  const [tab, setTab] = useState('wallet');
  const [page, setPage] = useState(1);
  const pageSize = 50;
  const [filters, setFilters] = useState({ brand: '', from: '', to: '' });
  const [downloading, setDownloading] = useState(false);
  const { options: brandOptions } = useBrands();

  const active = TABS.find((t) => t.key === tab);
  const params = {
    page, pageSize,
    audience,
    ...(filters.brand ? { brand: filters.brand } : {}),
    ...(filters.from ? { from: filters.from } : {}),
    ...(filters.to ? { to: filters.to } : {}),
  };

  const { data, isLoading } = useQuery({
    queryKey: ['reports', tab, params],
    queryFn: () => get(active.endpoint, { params }),
    placeholderData: keepPreviousData,
  });
  const rows = data?.data || [];
  const total = data?.meta?.total || 0;
  const summary = data?.meta?.summary;

  // Filters are all shared Select/DatePicker controls, whose onChange emits the
  // value directly (not a DOM event).
  const setF = (k) => (v) => { setFilters({ ...filters, [k]: v }); setPage(1); };

  const exportCsv = async () => {
    setDownloading(true);
    try {
      // Don't override pageSize here — the backend caps the CSV to its own
      // 10000-row bound, and sending pageSize=10000 exceeds the query schema's
      // max (1000) and gets rejected with a 400 ("Export failed").
      const res = await api.get(active.endpoint, { params: { ...params, format: 'csv' }, responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url; a.download = `${audience}-${tab}-report.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.err('Export failed', apiErrorMessage(e, 'Could not export.'));
    } finally {
      setDownloading(false);
    }
  };

  return (
    <>
      <PageHead
        title={title}
        subtitle={subtitle}
        actions={
          <button className="btn primary" onClick={exportCsv} disabled={downloading || !rows.length}>
            <Icon name="download" /> {downloading ? 'Exporting…' : 'Export CSV'}
          </button>
        }
      />

      <div className="seg" style={{ marginBottom: 14, width: 'fit-content' }}>
        {TABS.map((t) => (
          <button key={t.key} type="button" className={tab === t.key ? 'active' : undefined} onClick={() => { setTab(t.key); setPage(1); }}>{t.label}</button>
        ))}
      </div>

      <article className="panel glass">
        <div className="search-row">
          <div className="field">
            <label>Brand</label>
            <Select
              value={filters.brand}
              onChange={setF('brand')}
              options={[{ value: '', label: 'All brands' }, ...brandOptions]}
            />
          </div>
          <div className="field"><label>From</label><DatePicker value={filters.from} onChange={setF('from')} /></div>
          <div className="field"><label>To</label><DatePicker value={filters.to} onChange={setF('to')} min={filters.from} /></div>
        </div>

        {summary ? (
          <div className="mini-kpi" style={{ gridTemplateColumns: `repeat(${tab === 'gateway' ? 3 : 2}, minmax(160px, 1fr))`, marginBottom: 16 }}>
            {tab === 'gateway' ? (
              <>
                <article className="mk glass neo"><small>Captured</small><b className="grad-text">{money(summary.captured)}</b></article>
                <article className="mk glass neo"><small>Refunded</small><b>{money(summary.refunded)}</b></article>
                <article className="mk glass neo"><small>Failed</small><b>{summary.failed_count}</b></article>
              </>
            ) : null}
            {tab === 'refunds' ? (
              <>
                <article className="mk glass neo"><small>Refunded</small><b className="grad-text">{money(summary.refunded_amount)}</b></article>
                <article className="mk glass neo"><small>Pending / in-flight</small><b>{summary.pending_count}</b></article>
              </>
            ) : null}
          </div>
        ) : null}

        <div className="table-wrap">
          {isLoading ? <TableSkeleton /> : (
            <table className="table">
              {tab === 'wallet' ? <WalletHead /> : tab === 'gateway' ? <GatewayHead /> : <RefundHead />}
              <tbody>
                {rows.length === 0 ? (
                  <tr><td colSpan={10} className="empty">No records for these filters.</td></tr>
                ) : rows.map((r, i) => (
                  tab === 'wallet' ? <WalletRow key={i} r={r} />
                    : tab === 'gateway' ? <GatewayRow key={i} r={r} />
                      : <RefundRow key={i} r={r} />
                ))}
              </tbody>
            </table>
          )}
        </div>
        <Pager page={page} pageSize={pageSize} total={total} onPage={setPage} />
      </article>
    </>
  );
}

// Brand names come from the tenant's registry — a code with no registry row
// (e.g. an old record) falls back to the raw code via labelOf.
const BrandChip = ({ code }) => {
  const { labelOf } = useBrands();
  return <Chip tone="ok">{labelOf(code)}</Chip>;
};
const roleLabel = (r) => (r === 'b2b_owner' || r === 'b2b_user' ? 'B2B' : 'B2C');

const WalletHead = () => (
  <thead><tr><th>Date</th><th>Owner</th><th>Brand</th><th>Type</th><th>Debit</th><th>Credit</th><th>Balance</th><th>Reference</th></tr></thead>
);
const WalletRow = ({ r }) => (
  <tr>
    <td className="mono">{dt(r.created_at)}</td>
    <td>{r.owner_name || r.owner_email || '—'}<small className="muted db">{r.owner_type === 'business' ? 'Company pool' : 'User'}</small></td>
    <td><BrandChip code={r.brand} /></td>
    <td>{walletTypeLabel(r.type)}</td>
    <td className="mono">{r.direction === 'debit' ? money(r.amount) : '—'}</td>
    <td className="mono">{r.direction === 'credit' ? money(r.amount) : '—'}</td>
    <td className="mono">{money(r.balance_after)}</td>
    <td className="mono">{r.reference || '—'}</td>
  </tr>
);

const GatewayHead = () => (
  <thead><tr><th>Date</th><th>Product</th><th>Status</th><th>Gateway</th><th>Txn ID</th><th>Amount</th><th>Customer</th></tr></thead>
);
const GatewayRow = ({ r }) => {
  const { labelOf } = useBrands();
  return (
    <tr>
      <td className="mono">{dt(r.created_at)}</td>
      <td>{r.flow_type}{r.payment_type && r.payment_type !== 'payment' ? <small className="muted db">{r.payment_type}</small> : null}</td>
      <td><Chip tone={statusTone(r.status)}>{r.status}</Chip></td>
      <td>{r.payment_gateway || '—'}</td>
      <td className="mono">{r.transaction_id || '—'}</td>
      <td className="mono">{money(r.amount)}</td>
      <td>{r.user_email || '—'}<small className="muted db">{roleLabel(r.user_role)}{r.user_brand ? ` · ${labelOf(r.user_brand)}` : ''}</small></td>
    </tr>
  );
};

const RefundHead = () => (
  <thead><tr><th>Booked</th><th>Product</th><th>Reference</th><th>Brand</th><th>Paid</th><th>Booking</th><th>Refund Status</th><th>Refund Amt</th><th>Cancelled</th></tr></thead>
);
const RefundRow = ({ r }) => (
  <tr>
    <td className="mono">{dt(r.created_at)}</td>
    <td>{r.product}<small className="muted db">{roleLabel(r.user_role)}</small></td>
    <td className="mono">{r.booking_reference || '—'}</td>
    <td><BrandChip code={r.brand} /></td>
    <td className="mono">{money(r.total_paid)}</td>
    <td><Chip tone={statusTone(r.booking_status)}>{r.booking_status}</Chip></td>
    <td>{r.refund_status ? <Chip tone={statusTone(r.refund_status)}>{r.refund_status}</Chip> : <span className="muted">—</span>}</td>
    <td className="mono">{r.refund_amount != null ? money(r.refund_amount) : '—'}</td>
    <td className="mono">{dt(r.cancel_time)}</td>
  </tr>
);
